import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';

export interface AllocationSegment {
  id: string;
  label: ReactNode;
  /** Share of the whole, 0–100. */
  percent: number;
  /** Optional formatted amount shown in the legend. */
  valueLabel?: ReactNode;
  /** Render in a muted tone (e.g. unallocated remainder). */
  muted?: boolean;
}

const SEGMENT_COLORS = [
  'bg-fin-accent',
  'bg-fin-green',
  'bg-fin-purple',
  'bg-fin-warning',
  'bg-fin-cyan',
  'bg-fin-danger',
] as const;

function colorFor(segment: AllocationSegment, index: number): string {
  if (segment.muted) return 'bg-fin-gray-700';
  return SEGMENT_COLORS[index % SEGMENT_COLORS.length];
}

export function segmentColorAtIndex(index: number, muted = false): string {
  if (muted) return 'bg-fin-gray-700';
  return SEGMENT_COLORS[index % SEGMENT_COLORS.length];
}

/**
 * A single horizontal 100% bar split into labelled segments, with a clickable
 * legend below. Used for estate allocation across parties (and reusable for any
 * "share of a whole" breakdown). Selecting a segment is optional.
 */
export function StackedAllocationBar({
  segments,
  selectedId,
  onSelect,
  onRemove,
  removeLabel,
  ariaLabel,
}: {
  segments: AllocationSegment[];
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  onRemove?: (id: string) => void;
  removeLabel?: string;
  ariaLabel?: string;
}) {
  const visible = segments.filter((s) => s.percent > 0);

  return (
    <div className='space-y-3'>
      <div
        className='bg-fin-gray-800 flex h-3 w-full overflow-hidden rounded-full'
        role='img'
        aria-label={ariaLabel}
      >
        {visible.map((segment, index) => (
          <div
            key={segment.id}
            className={cn(
              'h-full transition-opacity',
              colorFor(segment, index),
              selectedId && selectedId !== segment.id && 'opacity-40'
            )}
            style={{ width: `${Math.min(segment.percent, 100)}%` }}
          />
        ))}
      </div>
      {segments.length > 0 ? (
        <ul className='flex flex-col gap-1'>
          {segments.map((segment, index) => {
            const selected = selectedId === segment.id;
            const canRemove = Boolean(onRemove) && !segment.muted;
            const content = (
              <>
                <span
                  className={cn('size-2.5 shrink-0 rounded-sm', colorFor(segment, index))}
                  aria-hidden
                />
                <span className='min-w-0 flex-1 truncate'>{segment.label}</span>
                {segment.valueLabel ? (
                  <span className='text-muted-foreground shrink-0 whitespace-nowrap tabular-nums'>
                    {segment.valueLabel}
                  </span>
                ) : null}
                <span className='text-fin-white w-12 shrink-0 text-right font-medium whitespace-nowrap tabular-nums'>
                  {segment.percent.toFixed(1)}%
                </span>
              </>
            );
            const removeButton = canRemove ? (
              <button
                type='button'
                className='text-muted-foreground hover:text-fin-white shrink-0 rounded-md p-1'
                aria-label={removeLabel}
                onClick={(event) => {
                  event.stopPropagation();
                  onRemove?.(segment.id);
                }}
              >
                <X className='size-3.5' aria-hidden />
              </button>
            ) : null;

            return (
              <li key={segment.id}>
                {onSelect ? (
                  <div
                    className={cn(
                      'flex w-full min-w-0 items-center gap-1 rounded-md pr-1 text-sm transition-colors',
                      selected ? 'bg-fin-gray-800' : 'hover:bg-fin-gray-800/50'
                    )}
                  >
                    <button
                      type='button'
                      onClick={() => onSelect(segment.id)}
                      aria-pressed={selected}
                      className='flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-left'
                    >
                      {content}
                    </button>
                    {removeButton}
                  </div>
                ) : (
                  <div className='flex w-full min-w-0 items-center gap-1 px-2 py-1.5 pr-1 text-sm'>
                    <div className='flex min-w-0 flex-1 items-center gap-2'>{content}</div>
                    {removeButton}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
